
Copy

<template>
  <div class="readings-container l">
    <div class="section-header">
      <span class="section-title">Consumption & Archives</span>
      <div class="header-actions">
        <button class="action-button" :disabled="exportingCSV" @click="exportCsv">
          <svg v-if="!exportingCSV" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          {{ exportingCSV ? 'Exporting…' : 'Export CSV' }}
        </button>
        <button class="action-button" @click="viewAllReadings">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          View All
        </button>
      </div>
    </div>

    <!-- Granularity Tabs -->
    <div class="controls-row">
      <div class="granularity-tabs">
        <button
            v-for="tab in availableTabs"
            :key="tab.key"
            class="granularity-tab"
            :class="{ active: selectedGranularity === tab.key }"
            @click="selectGranularity(tab.key)"
        >
          <span class="tab-icon">{{ tab.icon }}</span>
          <span class="tab-label">{{ tab.label }}</span>
        </button>
      </div>

      <div class="data-info">
        <span class="stat-item">
          <span class="stat-value">{{ selectedData.length }}</span>
          <span class="stat-label">of {{ sourceData.length }} records</span>
        </span>
        <span class="stat-divider">|</span>
        <span class="stat-item">
          <span class="stat-label">{{ displayDateRange }}</span>
        </span>
        <button class="reset-btn" @click="resetRange" v-if="!isFullRange">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
          Reset
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading readings...</div>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-message">{{ error }}</div>
        <button class="retry-button" @click="fetchAllData">Retry</button>
      </div>

      <div v-else-if="sourceData.length === 0" class="no-data-state">
        <div class="no-data-icon">{{ emptyStateIcon }}</div>
        <div class="no-data-text">{{ emptyStateText }}</div>
        <div class="no-data-description">{{ emptyStateDescription }}</div>
      </div>

      <div v-else class="graph-table-row">
        <!-- Chart with Range Selector -->
        <div class="chart-section" :style="{ height: containerHeight + 'px' }">
          <!-- Main Chart -->
          <div class="main-chart">
            <Line :data="mainChartData" :options="mainChartOptions" :key="'main-' + chartKey" />
          </div>

          <!-- Range Selector (Mini Chart with Brush) -->
          <div class="range-selector">
            <div class="range-chart">
              <Line :data="rangeChartData" :options="rangeChartOptions" :key="'range-' + chartKey" />
            </div>
            <!-- Draggable Range Overlay -->
            <div class="range-overlay" ref="rangeOverlay">
              <div
                  class="range-inactive left"
                  :style="{ width: rangeLeftPercent + '%' }"
              ></div>
              <div
                  class="range-handle left"
                  :style="{ left: rangeLeftPercent + '%' }"
                  @mousedown="startDrag('left', $event)"
                  @touchstart="startDrag('left', $event)"
              >
                <div class="handle-grip"></div>
              </div>
              <div
                  class="range-active"
                  :style="{ left: rangeLeftPercent + '%', width: rangeWidthPercent + '%' }"
                  @mousedown="startDrag('middle', $event)"
                  @touchstart="startDrag('middle', $event)"
              ></div>
              <div
                  class="range-handle right"
                  :style="{ left: rangeRightPercent + '%' }"
                  @mousedown="startDrag('right', $event)"
                  @touchstart="startDrag('right', $event)"
              >
                <div class="handle-grip"></div>
              </div>
              <div
                  class="range-inactive right"
                  :style="{ left: rangeRightPercent + '%', width: (100 - rangeRightPercent) + '%' }"
              ></div>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="values-table-container" :style="{ height: containerHeight + 'px' }">
          <div class="readings-table">
            <div class="table-header" :class="tableHeaderClass">
              <div class="table-cell">CONSUMPTION</div>
              <div class="table-cell sortable" @click="toggleDateSort">
                DATE
                <span class="sort-icon">
                  <svg v-if="tableSortOrder === 'desc'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14"/>
                    <path d="m19 12-7 7-7-7"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 19V5"/>
                    <path d="m5 12 7-7 7 7"/>
                  </svg>
                </span>
              </div>
              <div class="table-cell">TIME</div>
              <div class="table-cell" v-if="showMeterValueColumn">METER VALUE</div>
              <div class="table-cell" v-if="showDiagnosticsColumn">STATUS</div>
            </div>
            <div class="table-body">
              <div
                  v-for="(item, index) in paginatedData"
                  :key="index"
                  class="table-row"
                  :class="tableRowClass"
              >
                <div class="table-cell value-cell">
                  <span :class="getConsumptionClass(item)">
                    {{ formatConsumption(item) }}
                  </span>
                </div>
                <div class="table-cell">{{ formatDate(item.timestamp) }}</div>
                <div class="table-cell">{{ formatTableTime(item.timestamp) }}</div>
                <div class="table-cell" v-if="showMeterValueColumn">
                  {{ formatMeterValue(item) }}
                </div>
                <div class="table-cell" v-if="showDiagnosticsColumn">
                  <span class="badge" :class="item.alarmClass">
                    {{ item.diagnostic }}
                  </span>
                </div>
              </div>
            </div>
            <div class="pagination-controls">
              <div class="pagination-info">
                Showing {{ startItem }}-{{ endItem }} of {{ selectedData.length }} records
                (Page {{ currentPage }} of {{ totalPages }})
              </div>
              <div class="pagination-buttons">
                <button
                    class="pagination-button"
                    :disabled="currentPage === 1"
                    @click="changePage(currentPage - 1)"
                >
                  Previous
                </button>
                <div class="page-numbers">
                  <button
                      v-for="page in visiblePageNumbers"
                      :key="page"
                      :class="['page-number', { active: currentPage === page }]"
                      @click="changePage(page)"
                  >
                    {{ page }}
                  </button>
                </div>
                <button
                    class="pagination-button"
                    :disabled="currentPage === totalPages"
                    @click="changePage(currentPage + 1)"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAlarmStatus } from '@/services/alarmHelper';
import {
  amrRawToM3,
  archiveValueToM3,
  getArchiveRawValue,
  isUnavailableArchiveItem
} from '@/services/loraWbusHelper';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Filler
} from 'chart.js';
import { Line } from 'vue-chartjs';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Tooltip,
    Filler
);

export default {
  name: 'DeviceDetailsReadings',
  components: {
    Line
  },
  props: {
    deviceId: {
      type: String,
      required: true
    },
    deviceProtocol: {
      type: String,
      default: ''
    },
    deviceSystemTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // Granularity selection
      selectedGranularity: 'daily',
      granularityTabs: [
        { key: 'hourly', label: 'Hourly', icon: '⏱' },
        { key: 'daily', label: 'Daily', icon: '📅' },
        { key: 'weekly', label: 'Weekly', icon: '📊' }
      ],

      // Table sort order (desc = newest first, asc = oldest first)
      tableSortOrder: 'desc',

      // Range selection (0-100 percent)
      rangeStart: 0,
      rangeEnd: 100,

      // Dragging state
      isDragging: false,
      dragType: null,
      dragStartX: 0,
      dragStartRangeStart: 0,
      dragStartRangeEnd: 0,

      // Raw data storage
      allReadings: [],
      allHourlyData: [],
      allWeeklyData: [],

      // Loading states
      isLoading: false,
      error: '',
      exportingCSV: false,

      // Layout
      containerHeight: 350,
      rowHeight: 48,
      tableHeaderHeight: 46,
      paginationHeight: 65,

      // Pagination
      currentPage: 1,

      // Chart key for forcing re-render
      chartKey: 0
    };
  },
  computed: {
    isLoraWbus() {
      const protocol = String(this.deviceProtocol || '').toLowerCase();
      return protocol === 'lora' || protocol === 'wbus' || protocol === 'lora_wbus';
    },

    isDLMS() {
      return this.deviceProtocol && this.deviceProtocol.toLowerCase() === 'dlms';
    },

    // Devices that support hourly granularity
    supportsHourly() {
      return this.isLoraWbus || this.isDLMS;
    },

    availableTabs() {
      if (this.isLoraWbus) {
        // LoRa WBus supports hourly, daily, and weekly
        return [
          { key: 'hourly', label: 'Hourly', icon: '⏱' },
          { key: 'daily', label: 'Daily', icon: '📅' },
          { key: 'weekly', label: 'Weekly', icon: '📊' }
        ];
      }
      if (this.isDLMS) {
        // DLMS supports hourly and daily only
        return [
          { key: 'hourly', label: 'Hourly', icon: '⏱' },
          { key: 'daily', label: 'Daily', icon: '📅' }
        ];
      }
      return [{ key: 'daily', label: 'Daily', icon: '📅' }];
    },

    // Range percentages for UI
    rangeLeftPercent() {
      return this.rangeStart;
    },

    rangeRightPercent() {
      return this.rangeEnd;
    },

    rangeWidthPercent() {
      return this.rangeEnd - this.rangeStart;
    },

    isFullRange() {
      return this.rangeStart === 0 && this.rangeEnd === 100;
    },

    // Source data based on granularity (with consumption calculated)
    sourceData() {
      switch (this.selectedGranularity) {
        case 'hourly':
          return this.allHourlyData;
        case 'weekly':
          return this.allWeeklyData;
        default:
          return this.allReadings;
      }
    },

    // Selected data based on range
    // Note: sourceData is sorted newest-first, but the range selector displays oldest-first (left-to-right)
    // So we need to reverse the percentage calculation
    selectedData() {
      const data = this.sourceData;
      if (!data || data.length === 0) return [];

      // Convert visual percentages (where 0% = oldest = left side)
      // to array indices (where 0 = newest)
      // rangeStart=0 visually means "start from oldest" = end of array
      // rangeStart=100 visually means "start from newest" = start of array
      const visualStartPercent = this.rangeStart;
      const visualEndPercent = this.rangeEnd;

      // Invert the percentages for the array (which is newest-first)
      const arrayStartPercent = 100 - visualEndPercent;
      const arrayEndPercent = 100 - visualStartPercent;

      const startIndex = Math.floor((arrayStartPercent / 100) * data.length);
      const endIndex = Math.ceil((arrayEndPercent / 100) * data.length);

      return data.slice(startIndex, endIndex);
    },

    // Sorted data for table display
    sortedTableData() {
      if (this.selectedData.length === 0) return [];

      // Create a copy and sort by full timestamp
      const sorted = [...this.selectedData].sort((a, b) => {
        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();

        if (this.tableSortOrder === 'desc') {
          return dateB - dateA; // Newest first
        } else {
          return dateA - dateB; // Oldest first
        }
      });

      return sorted;
    },

    // Display date range
    displayDateRange() {
      if (this.selectedData.length === 0) return 'No data';

      const newest = this.selectedData[0];
      const oldest = this.selectedData[this.selectedData.length - 1];

      return `${this.formatDate(oldest.timestamp)} → ${this.formatDate(newest.timestamp)}`;
    },

    // Empty states
    emptyStateIcon() {
      const icons = { hourly: '⏱', daily: '📅', weekly: '📊' };
      return icons[this.selectedGranularity] || '📊';
    },

    emptyStateText() {
      const texts = { hourly: 'No Hourly Data', daily: 'No Daily Readings', weekly: 'No Weekly Data' };
      return texts[this.selectedGranularity] || 'No Data';
    },

    emptyStateDescription() {
      const descriptions = {
        hourly: 'Hourly consumption data will appear here once collected',
        daily: 'Daily consumption data will appear here once collected',
        weekly: 'Weekly consumption data will appear here once collected'
      };
      return descriptions[this.selectedGranularity] || 'Data will appear here once collected';
    },

    // Table columns
    showMeterValueColumn() {
      return this.selectedGranularity === 'daily' || this.selectedGranularity === 'weekly';
    },

    showDiagnosticsColumn() {
      return this.selectedGranularity === 'daily';
    },

    tableHeaderClass() {
      let cols = 3; // consumption, date, time
      if (this.showMeterValueColumn) cols++;
      if (this.showDiagnosticsColumn) cols++;
      return `columns-${cols}`;
    },

    tableRowClass() {
      let cols = 3;
      if (this.showMeterValueColumn) cols++;
      if (this.showDiagnosticsColumn) cols++;
      return `columns-${cols}`;
    },

    // Pagination
    rowsPerContainer() {
      const availableHeight = this.containerHeight - this.tableHeaderHeight - this.paginationHeight - 60;
      const rows = Math.floor(availableHeight / this.rowHeight);
      return Math.max(3, rows);
    },

    perPage() {
      return this.rowsPerContainer;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.sortedTableData.length / this.perPage));
    },

    startItem() {
      return this.sortedTableData.length === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    },

    endItem() {
      return Math.min(this.currentPage * this.perPage, this.sortedTableData.length);
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.sortedTableData.slice(start, end);
    },

    visiblePageNumbers() {
      const maxVisible = 5;
      const half = Math.floor(maxVisible / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end === this.totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    // Chart data with smart gap detection
    // Shows gaps in data without generating excessive points
    mainChartData() {
      if (this.selectedData.length === 0) {
        return { labels: [], datasets: [] };
      }

      // Sort chronologically for chart display (oldest first)
      const chartItems = [...this.selectedData].reverse();

      // Get interval in milliseconds based on granularity
      const intervalMs = this.selectedGranularity === 'hourly'
          ? 60 * 60 * 1000  // 1 hour
          : this.selectedGranularity === 'weekly'
              ? 7 * 24 * 60 * 60 * 1000  // 7 days
              : 24 * 60 * 60 * 1000;  // 1 day (default)

      // Tolerance for gap detection (1.5x the interval)
      const gapThreshold = intervalMs * 1.5;

      // Build chart data with gap markers
      const labels = [];
      const consumptionData = [];
      const gapIndices = []; // Track where gaps are for potential markers

      let lastTimestamp = null;

      chartItems.forEach((item) => {
        const currentTimestamp = new Date(item.timestamp).getTime();

        // Check if there's a gap before this point
        if (lastTimestamp !== null) {
          const timeDiff = currentTimestamp - lastTimestamp;

          // If gap is larger than threshold, insert a null point to break the line
          if (timeDiff > gapThreshold) {
            // Calculate how many intervals are missing
            const missingIntervals = Math.floor(timeDiff / intervalMs) - 1;

            // Only insert gap marker if not too many (max 50 gap markers total)
            if (missingIntervals > 0 && gapIndices.length < 50) {
              // Insert a single null point to create visual gap
              labels.push('');
              consumptionData.push(null);
              gapIndices.push(labels.length - 1);
            }
          }
        }

        // Add actual data point - ensure we get a valid number
        labels.push(this.formatChartLabel(item.timestamp));
        const consumptionValue = Number(item.consumption) || 0;
        consumptionData.push(Math.max(0, consumptionValue));

        lastTimestamp = currentTimestamp;
      });

      const datasets = [{
        label: 'Consumption (m³)',
        data: consumptionData,
        borderColor: '#4fc3f7',
        backgroundColor: 'rgba(79, 195, 247, 0.2)',
        tension: 0.3,
        pointRadius: chartItems.length > 100 ? 0 : chartItems.length > 50 ? 2 : 3,
        pointHoverRadius: 6,
        pointBackgroundColor: '#4fc3f7',
        pointHoverBackgroundColor: '#4fc3f7',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
        fill: true,
        borderWidth: 2,
        spanGaps: false // Don't connect across null values
      }];

      // Add gap markers dataset if there are gaps (and not too many)
      if (gapIndices.length > 0 && gapIndices.length <= 50) {
        const gapData = consumptionData.map((val, idx) =>
            gapIndices.includes(idx) ? 0 : null
        );

        datasets.push({
          label: 'Missing Data',
          data: gapData,
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#ef5350',
          pointBorderColor: '#ef5350',
          pointBorderWidth: 2,
          pointStyle: 'crossRot',
          fill: false,
          showLine: false
        });
      }

      return { labels, datasets };
    },

    mainChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            titleColor: 'white',
            bodyColor: 'white',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            cornerRadius: 8,
            padding: 12,
            displayColors: false,
            filter: function(tooltipItem) {
              // Don't show tooltip for null values (gaps)
              return tooltipItem.parsed.y !== null;
            },
            callbacks: {
              title: function(context) {
                // Skip if this is a gap marker
                if (context[0].datasetIndex === 1) {
                  return 'Missing Reading';
                }
                const label = context[0].label;
                if (!label) return 'Gap in data';
                return label;
              },
              label: function(context) {
                // Skip if this is a gap marker
                if (context.datasetIndex === 1) {
                  return '⚠ No data recorded for this period';
                }
                if (context.parsed.y === null) {
                  return '⚠ Missing reading';
                }
                const val = context.parsed.y ?? 0;
                return `Consumption: ${val.toFixed(3)} m³`;
              },
              afterLabel: function(context) {
                // Skip for gap markers
                if (context.datasetIndex === 1 || context.parsed.y === null) {
                  return '';
                }
                // We can't easily get meter value here due to gap insertions
                // Just return empty for now
                return '';
              }
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        },
        scales: {
          x: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 10 },
              maxTicksLimit: 8,
              maxRotation: 0
            },
            grid: { display: false },
            border: { display: false }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 10 },
              callback: function(value) {
                return (Number(value) || 0).toFixed(2) + ' m³';
              }
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.06)',
              drawTicks: false
            },
            border: { display: false }
          }
        }
      };
    },

    // Range selector chart data (all consumption data, mini version)
    rangeChartData() {
      if (this.sourceData.length === 0) {
        return { labels: [], datasets: [] };
      }

      const chartItems = [...this.sourceData].reverse();
      const labels = chartItems.map(item => this.formatChartLabel(item.timestamp));
      const data = chartItems.map(item => Math.max(0, item.consumption || 0));

      return {
        labels,
        datasets: [{
          data,
          borderColor: 'rgba(79, 195, 247, 0.5)',
          backgroundColor: 'rgba(79, 195, 247, 0.15)',
          tension: 0.3,
          pointRadius: 0,
          fill: true,
          borderWidth: 1
        }]
      };
    },

    rangeChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        },
        interaction: { intersect: false, mode: 'index' },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true }
        }
      };
    }
  },
  watch: {
    deviceId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchAllData();
        }
      }
    },
    selectedGranularity() {
      this.resetRange();
      this.chartKey++;
    },
    selectedData() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = 1;
      }
    }
  },
  mounted() {
    this.calculateContainerHeight();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('mousemove', this.onDrag);
    window.addEventListener('mouseup', this.stopDrag);
    window.addEventListener('touchmove', this.onDrag);
    window.addEventListener('touchend', this.stopDrag);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('mousemove', this.onDrag);
    window.removeEventListener('mouseup', this.stopDrag);
    window.removeEventListener('touchmove', this.onDrag);
    window.removeEventListener('touchend', this.stopDrag);
  },
  methods: {
    handleResize() {
      this.calculateContainerHeight();
      this.chartKey++;
    },

    calculateContainerHeight() {
      const container = this.$el;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const availableHeight = window.innerHeight - containerRect.top - 70;

      const sectionHeader = container.querySelector('.section-header');
      const controlsRow = container.querySelector('.controls-row');

      const headerHeight = sectionHeader ? sectionHeader.offsetHeight : 60;
      const controlsHeight = controlsRow ? controlsRow.offsetHeight : 60;
      const gaps = 40;

      const chartHeight = availableHeight - headerHeight - controlsHeight - gaps;
      this.containerHeight = Math.max(280, chartHeight);
    },

    selectGranularity(granularity) {
      if (!this.supportsHourly && granularity !== 'daily') return;
      this.selectedGranularity = granularity;
    },

    toggleDateSort() {
      this.tableSortOrder = this.tableSortOrder === 'desc' ? 'asc' : 'desc';
      this.currentPage = 1;
    },

    resetRange() {
      this.rangeStart = 0;
      this.rangeEnd = 100;
      this.currentPage = 1;
      this.chartKey++;
    },

    // Drag handlers
    startDrag(type, event) {
      event.preventDefault();
      this.isDragging = true;
      this.dragType = type;

      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      this.dragStartX = clientX;
      this.dragStartRangeStart = this.rangeStart;
      this.dragStartRangeEnd = this.rangeEnd;
    },

    onDrag(event) {
      if (!this.isDragging) return;

      const overlay = this.$refs.rangeOverlay;
      if (!overlay) return;

      const rect = overlay.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const deltaX = clientX - this.dragStartX;
      const deltaPercent = (deltaX / rect.width) * 100;

      const minRange = 5;

      if (this.dragType === 'left') {
        let newStart = this.dragStartRangeStart + deltaPercent;
        newStart = Math.max(0, Math.min(newStart, this.rangeEnd - minRange));
        this.rangeStart = newStart;
      } else if (this.dragType === 'right') {
        let newEnd = this.dragStartRangeEnd + deltaPercent;
        newEnd = Math.min(100, Math.max(newEnd, this.rangeStart + minRange));
        this.rangeEnd = newEnd;
      } else if (this.dragType === 'middle') {
        const rangeWidth = this.dragStartRangeEnd - this.dragStartRangeStart;
        let newStart = this.dragStartRangeStart + deltaPercent;
        let newEnd = this.dragStartRangeEnd + deltaPercent;

        if (newStart < 0) {
          newStart = 0;
          newEnd = rangeWidth;
        }
        if (newEnd > 100) {
          newEnd = 100;
          newStart = 100 - rangeWidth;
        }

        this.rangeStart = newStart;
        this.rangeEnd = newEnd;
      }

      this.chartKey++;
    },

    stopDrag() {
      this.isDragging = false;
      this.dragType = null;
    },

    formatDiagnosticValue(value) {
      if (value == null || value === '') return '';

      if (Array.isArray(value)) {
        return value
            .map(item => this.formatDiagnosticValue(item))
            .filter(Boolean)
            .join(', ');
      }

      if (typeof value === 'object') {
        const name = String(value.name || value.Name || value.code || value.Code || '').trim();
        const description = String(
            value.description ||
            value.Description ||
            value.label ||
            value.Label ||
            ''
        ).trim();

        if (description && description !== name) return description;
        if (name) return name;

        return Object.entries(value)
            .filter(([, itemValue]) => itemValue != null && itemValue !== '')
            .map(([key, itemValue]) => `${key}: ${this.formatDiagnosticValue(itemValue)}`)
            .join(', ');
      }

      return String(value).trim();
    },

    formatAlarmStatus(status) {
      if (!status?.hasAlarms) return 'NO ALARMS';

      const alarms = Array.isArray(status.alarms)
          ? status.alarms.map(alarm => this.formatDiagnosticValue(alarm)).filter(Boolean)
          : [];

      return alarms.length ? alarms.join(', ') : 'ALARM';
    },

    async fetchAllData() {
      if (!this.deviceId) return;

      this.isLoading = true;
      this.error = '';

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;

        if (this.isDLMS) {
          // DLMS uses a different endpoint with systemTitle
          if (!this.deviceSystemTitle) {
            throw new Error('System Title required for DLMS device');
          }

          const url = `${apiBaseUrl}/dlms/readings/systemtitle/${encodeURIComponent(this.deviceSystemTitle)}?limit=500&skip=0`;

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });

          if (!response.ok) {
            throw new Error(`Error fetching DLMS readings: ${response.statusText}`);
          }

          const data = await response.json();
          this.processDLMSReadings(data.readings || []);
        } else {
          // Standard readings endpoint for LoRa/WBus
          const response = await fetch(
              `${apiBaseUrl}/readings/device/${this.deviceId}?limit=500&skip=0`,
              {
                method: 'GET',
                headers: {
                  'accept': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              }
          );

          if (!response.ok) {
            throw new Error(`Error fetching readings: ${response.statusText}`);
          }

          const data = await response.json();
          this.processReadings(data.readings || []);
        }

      } catch (error) {
        console.error('Error fetching readings:', error);
        this.error = 'Failed to load readings';
      } finally {
        this.isLoading = false;
      }
    },

    processReadings(readings) {
      // Helper to extract date from MongoDB { $date: "..." } or standard string
      const parseDate = (dateObj) => {
        if (dateObj?.$date) return new Date(dateObj.$date);
        return new Date(dateObj);
      };

      const readingToMeterValue = (reading) => {
        const currentReading = Number(reading?.current_reading);

        if (this.isLoraWbus) {
          const additionalData = reading?.additional_data || {};
          const rawValue = additionalData.Volume?.Raw ?? reading?.current_reading;
          const m3Value = amrRawToM3(rawValue, additionalData);

          if (m3Value !== null) return m3Value;

          const omsValue = Number(additionalData.total_m3 ?? additionalData.total_volume_m3);
          if (Number.isFinite(omsValue)) return omsValue;
        }

        return Number.isFinite(currentReading) ? currentReading : 0;
      };

      // 1. Sort readings by date (newest first)
      const sortedReadings = [...readings].sort(
          (a, b) => parseDate(b.date_current_reading) - parseDate(a.date_current_reading)
      );

      // 2. Process Daily Readings
      this.allReadings = sortedReadings.map((r, index) => {
        const status = getAlarmStatus(r);
        const currentValue = readingToMeterValue(r);

        let consumption = 0;
        if (index < sortedReadings.length - 1) {
          const nextValue = readingToMeterValue(sortedReadings[index + 1]);
          consumption = currentValue - nextValue;
        }

        return {
          type: 'daily',
          meterValue: currentValue,
          consumption: Number.isFinite(consumption) ? consumption : 0,
          timestamp: parseDate(r.date_current_reading),
          diagnostic: this.formatAlarmStatus(status),
          alarmClass: status.hasAlarms ? 'badge-error' : '',
          original: r
        };
      });

      // 3. Process Hourly/Weekly (LoRa)
      if (this.isLoraWbus) {
        const hourlyMap = new Map();
        const weeklyMap = new Map();

        readings.forEach(reading => {
          const additionalData = reading?.additional_data || {};

          // --- HOURLY ARCHIVE ---
          if (additionalData.HourlyArchive) {
            additionalData.HourlyArchive.forEach(hour => {
              if (!isUnavailableArchiveItem(hour, 'hourly') && !hourlyMap.has(hour.Timestamp)) {
                // Convert Liters to m3 to match chart scale
                const hCons = archiveValueToM3(hour, additionalData) || 0;
                hourlyMap.set(hour.Timestamp, {
                  type: 'hourly',
                  consumption: hCons,
                  meterValue: 0,
                  timestamp: hour.Timestamp,
                  original: hour
                });
              }
            });
          }

          // --- WEEKLY ARCHIVE ---
          if (additionalData.WeeklyArchive) {
            additionalData.WeeklyArchive.forEach(week => {
              if (!isUnavailableArchiveItem(week, 'weekly') && !weeklyMap.has(week.Timestamp)) {
                // Engineered here is the absolute meter index in Liters -> Convert to m3
                const wVal = archiveValueToM3(week, additionalData) || 0;
                weeklyMap.set(week.Timestamp, {
                  type: 'weekly',
                  meterValue: wVal,
                  consumption: 0,
                  timestamp: week.Timestamp,
                  original: week
                });
              }
            });
          }
        });

        // Sort and convert Hourly
        this.allHourlyData = Array.from(hourlyMap.values())
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        // Sort and calculate Weekly Consumption
        const weeklyArray = Array.from(weeklyMap.values())
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        this.allWeeklyData = weeklyArray.map((week, index) => {
          let consumption = 0;
          if (index < weeklyArray.length - 1) {
            // Current week index - Previous week index
            consumption = week.meterValue - weeklyArray[index + 1].meterValue;
          }
          return {
            ...week,
            consumption: Number.isFinite(consumption) ? consumption : 0
          };
        });
      }
    },

    processDLMSReadings(readings) {
      // Helper to find a voluntary_data entry by key pattern
      const findVoluntaryData = (voluntaryData, keyPattern) => {
        if (!voluntaryData || !Array.isArray(voluntaryData)) return null;
        for (const item of voluntaryData) {
          const key = Object.keys(item).find(k => k.includes(keyPattern));
          if (key) {
            return item[key];
          }
        }
        return null;
      };

      // Helper to convert array of {Key, Value} objects to a flat object
      // Handles both formats:
      // 1. Array format: [{ Key: 'Unix Time', Value: '...' }, ...]
      // 2. Object format: { 'Unix Time': '...', 'Flow Rates': [...] }
      const normalizeProfile = (profile) => {
        if (!profile) return null;

        // If it's already an object with the expected keys, return as-is
        if (!Array.isArray(profile) && typeof profile === 'object') {
          if (profile['Unix Time'] || profile['Flow Rates']) {
            return profile;
          }
        }

        // If it's an array of {Key, Value} objects, convert to flat object
        if (Array.isArray(profile)) {
          const normalized = {};
          profile.forEach(item => {
            if (item && item.Key !== undefined) {
              normalized[item.Key] = item.Value;
            }
          });
          return normalized;
        }

        return null;
      };

      const hourlyMap = new Map();
      const dailyMap = new Map();

      // Process each DLMS reading document
      readings.forEach((reading) => {
        const voluntaryData = reading.voluntary_data;
        if (!voluntaryData) return;

        // Find Daily Load Profile buffer
        const dailyLoadProfile = findVoluntaryData(voluntaryData, 'Daily Load Profile.buffer');

        if (dailyLoadProfile && Array.isArray(dailyLoadProfile)) {
          dailyLoadProfile.forEach((entry) => {
            // Handle both: entry.converted_value or entry directly
            const rawProfile = entry.converted_value || entry;
            const profile = normalizeProfile(rawProfile);

            if (!profile) return;

            const unixTime = profile['Unix Time'];
            if (!unixTime) return;

            const dayTimestamp = new Date(unixTime);

            // Validate timestamp - skip if invalid or before year 2000 (clearly wrong data)
            if (isNaN(dayTimestamp.getTime()) || dayTimestamp.getFullYear() < 2000) {
              return;
            }

            const dayKey = dayTimestamp.toISOString().slice(0, 10); // YYYY-MM-DD

            // Extract daily total consumption (already in m³ based on scaler)
            const totalVolume = parseFloat(profile['Total Converted Volume']) || 0;
            const reverseVolume = parseFloat(profile['Total Converted Volume Reverse']) || 0;
            const alarmVolume = parseFloat(profile['Total Converted Volume Under Alarm']) || 0;
            const diagnostic = this.formatDiagnosticValue(profile['Current Diagnostic']);

            // Store daily data - totalVolume is the cumulative meter value, NOT daily consumption
            // We'll calculate actual consumption after sorting
            if (!dailyMap.has(dayKey)) {
              dailyMap.set(dayKey, {
                type: 'daily',
                meterValue: totalVolume, // Cumulative meter reading
                consumption: 0, // Will be calculated after sorting
                timestamp: dayTimestamp,
                diagnostic: diagnostic || 'NO ALARMS',
                alarmClass: diagnostic && diagnostic !== 'NO_ALARM' ? 'badge-error' : '',
                reverseVolume,
                alarmVolume,
                original: profile
              });
            }

            // Extract hourly flow rates (24 values per day)
            const flowRates = profile['Flow Rates'];
            if (flowRates && Array.isArray(flowRates) && flowRates.length === 24) {
              // Flow rates are hourly consumption values
              flowRates.forEach((rate, hourIndex) => {
                const hourConsumption = parseFloat(rate) || 0;

                // Create timestamp for this hour
                // The profile timestamp is typically at 22:00, and Flow Rates[0] is for hour 0 (00:00-01:00)
                const hourDate = new Date(dayTimestamp);
                hourDate.setHours(hourIndex, 0, 0, 0);

                // Validate hour timestamp
                if (isNaN(hourDate.getTime()) || hourDate.getFullYear() < 2000) {
                  return;
                }

                const hourKey = hourDate.toISOString();

                if (!hourlyMap.has(hourKey)) {
                  hourlyMap.set(hourKey, {
                    type: 'hourly',
                    consumption: hourConsumption,
                    meterValue: 0, // Hourly doesn't have meter value
                    timestamp: hourDate,
                    original: { rate, hourIndex, dayProfile: profile }
                  });
                }
              });
            }
          });
        }
      });

      // Convert maps to sorted arrays (newest first)
      const sortedDaily = Array.from(dailyMap.values())
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Calculate daily consumption as difference between consecutive meter values
      // Data is sorted newest first, so consumption = current - next (which is older)
      this.allReadings = sortedDaily.map((reading, index) => {
        let consumption = 0;
        if (index < sortedDaily.length - 1) {
          const olderReading = sortedDaily[index + 1];
          consumption = reading.meterValue - olderReading.meterValue;
        }
        return {
          ...reading,
          consumption: Number.isFinite(consumption) ? consumption : 0
        };
      });

      this.allHourlyData = Array.from(hourlyMap.values())
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // DLMS doesn't have weekly archives, clear it
      this.allWeeklyData = [];
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    formatConsumption(item) {
      const consumption = item?.consumption ?? 0;
      if (Math.abs(consumption) < 0.0001) return '0.000 m³';

      // toFixed(3) handles negative signs automatically
      // For positive values, we manually add the "+" sign for clarity
      const sign = consumption > 0 ? '+' : '';
      return `${sign}${consumption.toFixed(3)} m³`;
    },

    getConsumptionClass(item) {
      const consumption = item.consumption || 0;
      if (consumption > 0.0001) return 'consumption-positive';
      if (consumption < -0.0001) return 'consumption-negative';
      return '';
    },

    formatMeterValue(item) {
      if (item.meterValue === null || item.meterValue === undefined) return '-';
      return `${item.meterValue.toFixed(3)} m³`;
    },

    formatChartLabel(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);

      switch (this.selectedGranularity) {
        case 'hourly':
          return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:00`;
        case 'weekly':
          return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`;
        default:
          return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    },

    formatTime(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    },

    formatTableTime(timestamp) {
      if (!timestamp) return 'N/A';
      // For daily and weekly, show 00:00 to match chart alignment
      if (this.selectedGranularity === 'daily' || this.selectedGranularity === 'weekly') {
        return '00:00';
      }
      // For hourly, show the actual hour
      const date = new Date(timestamp);
      return `${String(date.getHours()).padStart(2, '0')}:00`;
    },

    formatFullDateTime(timestamp) {
      return `${this.formatDate(timestamp)} ${this.formatTime(timestamp)}`;
    },

    async fetchStandardReadingsForExport() {
      const apiBaseUrl = process.env.VUE_APP_API_URL;
      const pageSize = 500;
      const maxPages = 20;
      let skip = 0;
      let total = null;
      const readings = [];

      for (let page = 0; page < maxPages; page++) {
        const response = await fetch(
            `${apiBaseUrl}/readings/device/${encodeURIComponent(this.deviceId)}?limit=${pageSize}&skip=${skip}`,
            {
              method: 'GET',
              headers: {
                accept: 'application/json',
                Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
              },
            }
        );

        if (!response.ok) {
          throw new Error(`Export failed (${response.status})`);
        }

        const data = await response.json();
        const pageReadings = data.readings || [];
        readings.push(...pageReadings);
        total = Number.isFinite(Number(data.count)) ? Number(data.count) : total;

        if (!pageReadings.length || pageReadings.length < pageSize || (total !== null && readings.length >= total)) {
          break;
        }

        skip += pageReadings.length;
      }

      return readings;
    },

    hasSonataArchiveReadings(readings) {
      return readings.some(reading => {
        const additionalData = reading?.additional_data || {};
        const messageType = Number(additionalData.MessageType);
        return (
            messageType === 128 ||
            messageType === 129 ||
            Array.isArray(additionalData.HourlyArchive) ||
            Array.isArray(additionalData.WeeklyArchive) ||
            String(additionalData.FWVersion || '').trim().toUpperCase() === '4B.58'
        );
      });
    },

    getReadingDateValue(value) {
      if (value?.$date) return value.$date;
      return value;
    },

    getReadingTimestampMs(value) {
      const normalizedValue = this.getReadingDateValue(value);
      const timestamp = new Date(normalizedValue).getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    },

    formatCsvHumanDateTime(value) {
      const normalizedValue = this.getReadingDateValue(value);
      if (!normalizedValue) return '';

      const date = new Date(normalizedValue);
      if (isNaN(date.getTime())) return String(normalizedValue);

      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    getMeterTimestampValue(additionalData) {
      if (additionalData?.Timestamp) return additionalData.Timestamp;

      const unixTime = Number(additionalData?.UnixTime);
      if (Number.isFinite(unixTime) && unixTime > 0) return unixTime * 1000;

      return '';
    },

    format4B58Value(value, additionalData = {}) {
      if (value === null || value === undefined || value === '') return '';

      const number = amrRawToM3(value, additionalData);
      if (number === null) return value;

      return number.toFixed(3);
    },

    format4B58Metric(value, unit = '', additionalData = {}) {
      const formatted = this.format4B58Value(value, additionalData);
      if (formatted === '') return '';
      return unit ? `${formatted} ${unit}` : formatted;
    },

    format4B58Battery(value) {
      if (value === null || value === undefined || value === '') return '';

      const number = Number(value);
      if (!Number.isFinite(number)) return String(value);

      return `${number.toFixed(3)} V`;
    },

    format4B58Signal(value) {
      const number = Number(value);
      if (!Number.isFinite(number) || number === 0) return '';

      return `${number} dBm`;
    },

    format4B58FrameName(messageType) {
      const type = Number(messageType);
      if (type === 128) return 'Index frame (0x80)';
      if (type === 129) return 'Event frame (0x81)';
      return messageType === null || messageType === undefined || messageType === '' ? '' : String(messageType);
    },

    formatAlarmList(activeAlarms) {
      if (!Array.isArray(activeAlarms) || !activeAlarms.length) return 'NO ALARMS';

      return activeAlarms
          .map(alarm => {
            const name = alarm?.Name || alarm?.name || '';
            const description = alarm?.Description || alarm?.description || '';
            if (name && description) return `${name}: ${description}`;
            return name || description;
          })
          .filter(Boolean)
          .join(' | ');
    },

    format4B58Diagnostic(alarms) {
      const activeAlarms = Array.isArray(alarms?.Active) ? alarms.Active : [];
      if (activeAlarms.length) return this.formatAlarmList(activeAlarms);

      const bitmap = Number(alarms?.Bitmap || 0);
      return bitmap > 0 ? 'Alarm state reported' : 'NO ALARMS';
    },

    format4B58Archive(archive, archiveType = 'hourly', additionalData = {}) {
      if (!Array.isArray(archive) || !archive.length) return '';

      return archive
          .map(item => {
            const timestamp = this.formatCsvHumanDateTime(item?.Timestamp);
            const value = isUnavailableArchiveItem(item, archiveType)
                ? 'N/A'
                : this.format4B58Value(getArchiveRawValue(item), additionalData);
            const status = item?.Status && item.Status !== 'valid' ? ` (${item.Status})` : '';

            if (!timestamp && !value) return '';
            return `${timestamp} ${value}${status}`.trim();
          })
          .filter(Boolean)
          .join(', ');
    },

    format4B58Duration(value) {
      const totalMinutes = Number(value);
      if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '';

      const roundedMinutes = Math.round(totalMinutes);
      const days = Math.floor(roundedMinutes / 1440);
      const hours = Math.floor((roundedMinutes % 1440) / 60);
      const minutes = roundedMinutes % 60;
      const parts = [];

      if (days) parts.push(`${days}d`);
      if (hours) parts.push(`${hours}h`);
      if (minutes) parts.push(`${minutes}m`);

      return parts.length ? `${roundedMinutes} min (${parts.join(' ')})` : `${roundedMinutes} min`;
    },

    hasMeaningful4B58Event(eventValue) {
      if (!eventValue || typeof eventValue !== 'object') return false;

      const startTime = Number(eventValue.StartTime);
      const duration = Number(eventValue.Duration);
      const value = Number(eventValue.Engineered ?? eventValue.Raw);
      const status = String(eventValue.Status || '').trim().toLowerCase();

      return (
          (Number.isFinite(startTime) && startTime > 0) ||
          (Number.isFinite(duration) && duration > 0) ||
          (Number.isFinite(value) && value > 0) ||
          (status && status !== 'valid')
      );
    },

    format4B58Event(eventValue) {
      if (!this.hasMeaningful4B58Event(eventValue)) return '';

      const parts = [];
      const startTime = this.formatCsvHumanDateTime(Number(eventValue.StartTime) * 1000);
      const duration = this.format4B58Duration(eventValue.Duration);
      const value = eventValue.Engineered ?? eventValue.Raw;
      const status = eventValue.Status && eventValue.Status !== 'valid' ? eventValue.Status : '';

      if (startTime) parts.push(`started ${startTime}`);
      if (duration) parts.push(duration);
      if (value !== null && value !== undefined && value !== '') parts.push(String(value));
      if (status) parts.push(status);

      return parts.join(', ');
    },

    format4B58CurrentVolume(volume, reading, additionalData) {
      if (volume && typeof volume === 'object') {
        return this.format4B58Metric(volume.Raw ?? volume.Engineered, 'm3', additionalData);
      }

      const readingValue = Number(reading?.current_reading);
      if (!Number.isFinite(readingValue)) return '';

      const m3Value = amrRawToM3(readingValue, additionalData);
      if (m3Value === null) return `${readingValue.toFixed(3)} m3`;

      return `${m3Value.toFixed(3)} m3`;
    },

    format4B58BackwardTotalizer(eventValue, additionalData = {}) {
      if (!eventValue || typeof eventValue !== 'object') return '';

      return this.format4B58Metric(eventValue.Raw ?? eventValue.Engineered, 'm3', additionalData);
    },

    buildFirmware4B58Csv(readings) {
      const sortedReadings = [...readings].sort(
          (a, b) => this.getReadingTimestampMs(b.date_current_reading) - this.getReadingTimestampMs(a.date_current_reading)
      );

      const rows = [[
        'Unix Time',
        'Frame',
        'Current Diagnostic',
        'Total Converted Volume',
        'Backward Totalizer',
        'Battery Level',
        'Signal',
        'Backflow Event',
        'Dry Event',
        'Leakage Event',
        'Overflow Event',
        'Pipe Burst Event',
        'Temperature Event',
        'Hourly Archive (m3)',
        'Weekly Archive (m3)'
      ]];

      const eventFields = [
        ['BackflowEvent', 'Backflow Event'],
        ['DryEvent', 'Dry Event'],
        ['LeakageEvent', 'Leakage Event'],
        ['OverflowEvent', 'Overflow Event'],
        ['PipeBurstEvent', 'Pipe Burst Event'],
        ['TemperatureEvent', 'Temperature Event']
      ];

      sortedReadings.forEach(reading => {
        const additionalData = reading?.additional_data || {};
        const volume = additionalData.Volume || {};
        const meterTimestamp =
            this.getMeterTimestampValue(additionalData) ||
            reading.date_current_reading ||
            reading.created_at;
        const eventColumns = Object.fromEntries(
            eventFields.map(([field, label]) => [label, this.format4B58Event(additionalData[field])])
        );

        rows.push([
          this.formatCsvHumanDateTime(meterTimestamp),
          this.format4B58FrameName(additionalData.MessageType),
          this.format4B58Diagnostic(additionalData.Alarms),
          this.format4B58CurrentVolume(volume, reading, additionalData),
          this.format4B58BackwardTotalizer(additionalData.BackwardTotalizer, additionalData),
          this.format4B58Battery(additionalData.BatteryLevel),
          this.format4B58Signal(additionalData.RSSI),
          eventColumns['Backflow Event'],
          eventColumns['Dry Event'],
          eventColumns['Leakage Event'],
          eventColumns['Overflow Event'],
          eventColumns['Pipe Burst Event'],
          eventColumns['Temperature Event'],
          this.format4B58Archive(additionalData.HourlyArchive, 'hourly', additionalData),
          this.format4B58Archive(additionalData.WeeklyArchive, 'weekly', additionalData)
        ]);
      });

      return this.rowsToCsv(rows);
    },

    rowsToCsv(rows) {
      return rows.map(row => row.map(value => this.escapeCsvValue(value)).join(',')).join('\n');
    },

    escapeCsvValue(value) {
      if (value === null || value === undefined) return '';

      const stringValue = String(value);
      if (/[",\n\r]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    },

    downloadCsvText(csvContent, filename) {
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    async downloadBackendCsv(endpoint, filename) {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          accept: 'text/csv',
          Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }

      const csvBlob = await response.blob();
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    async exportCsv() {
      const protocol = this.deviceProtocol ? this.deviceProtocol.toLowerCase() : '';

      // Standard readings endpoint covers LoRa, WBUS and combined LoRa/WBUS devices.
      const isStandardReadingDevice = protocol === 'lora' || protocol === 'wbus' || protocol === 'lora_wbus';

      // Determine if this is a DLMS device
      const isDLMSDevice = protocol === 'dlms';

      if (isStandardReadingDevice) {
        if (!this.deviceId) {
          alert('Device ID missing — cannot export.');
          return;
        }

        this.exportingCSV = true;
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const endpoint = `${apiBaseUrl}/readings/device/${encodeURIComponent(this.deviceId)}/download`;
        const today = new Date().toISOString().slice(0, 10);

        try {
          const readings = await this.fetchStandardReadingsForExport();
          if (this.hasSonataArchiveReadings(readings)) {
            const csvContent = this.buildFirmware4B58Csv(readings);
            this.downloadCsvText(csvContent, `readings_${this.deviceId}_sonata_archives_${today}.csv`);
          } else {
            await this.downloadBackendCsv(endpoint, `readings_${this.deviceId}_${today}.csv`);
          }
        } catch (err) {
          console.error(err);
          alert(err.message || 'Unknown error while exporting CSV');
        } finally {
          this.exportingCSV = false;
        }
      }
      // For DLMS devices, use DLMS readings endpoint
      else if (isDLMSDevice) {
        if (!this.deviceSystemTitle) {
          alert('System Title missing — cannot export.');
          return;
        }

        this.exportingCSV = true;
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const endpoint = `${apiBaseUrl}/dlms/readings/systemtitle/${encodeURIComponent(this.deviceSystemTitle)}/export/csv`;
        const today = new Date().toISOString().slice(0, 10);

        try {
          await this.downloadBackendCsv(endpoint, `dlms_readings_${this.deviceSystemTitle}_${today}.csv`);
        } catch (err) {
          console.error(err);
          alert(err.message || 'Unknown error while exporting CSV');
        } finally {
          this.exportingCSV = false;
        }
      }
      // Unsupported protocol
      else {
        alert(`CSV export not supported for protocol: ${this.deviceProtocol || 'Unknown'}`);
      }
    },

    viewAllReadings() {
      this.$router.push(`/devices/${this.deviceId}/readings`);
    }
  }
};
</script>


<style scoped>
.readings-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-header {
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Controls Row */
.controls-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;
}

/* Granularity Tabs */
.granularity-tabs {
  display: flex;
  gap: 6px;
  background: rgba(10, 10, 10, 0.5);
  padding: 5px;
  border-radius: 10px;
}

.granularity-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
}

.granularity-tab:hover:not(.active) {
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.05);
}

.granularity-tab.active {
  background: rgba(79, 195, 247, 0.15);
  color: #4fc3f7;
}

.tab-icon {
  font-size: 14px;
}

.tab-label {
  font-weight: 600;
}

/* Data Info */
.data-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-value {
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.stat-value.consumption {
  color: #4ade80;
}

.stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

.stat-divider {
  color: rgba(255, 255, 255, 0.2);
}

.reset-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(248, 113, 113, 0.1);
  border: none;
  border-radius: 6px;
  color: #f87171;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.reset-btn:hover {
  background: rgba(248, 113, 113, 0.2);
}

/* Content */
.content-wrapper {
  flex: 1;
  overflow: hidden;
}

.graph-table-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
}

/* Chart Section */
.chart-section {
  display: flex;
  flex-direction: column;
  background: rgba(10, 10, 10, 0.3);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.03);
  padding: 16px;
  min-width: 0;
}

.main-chart {
  flex: 1;
  min-height: 0;
  position: relative;
}

/* Range Selector */
.range-selector {
  position: relative;
  height: 60px;
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 12px;
}

.range-chart {
  position: absolute;
  top: 12px;
  left: 0;
  right: 0;
  height: 48px;
}

.range-overlay {
  position: absolute;
  top: 12px;
  left: 0;
  right: 0;
  height: 48px;
  z-index: 10;
}

.range-inactive {
  position: absolute;
  top: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

.range-inactive.left {
  left: 0;
  border-radius: 4px 0 0 4px;
}

.range-inactive.right {
  border-radius: 0 4px 4px 0;
}

.range-active {
  position: absolute;
  top: 0;
  height: 100%;
  background: transparent;
  border-top: 2px solid #4fc3f7;
  border-bottom: 2px solid #4fc3f7;
  cursor: grab;
  box-sizing: border-box;
}

.range-active:active {
  cursor: grabbing;
}

.range-handle {
  position: absolute;
  top: 0;
  width: 12px;
  height: 100%;
  background: #4fc3f7;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11;
  border-radius: 3px;
  transform: translateX(-50%);
  transition: background 0.2s;
}

.range-handle:hover {
  background: #29b6f6;
}

.range-handle.left {
  border-radius: 4px 0 0 4px;
  transform: translateX(0);
}

.range-handle.right {
  border-radius: 0 4px 4px 0;
  transform: translateX(-100%);
}

.handle-grip {
  width: 4px;
  height: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.handle-grip::before,
.handle-grip::after {
  content: '';
  width: 2px;
  height: 2px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
}

/* Table */
.values-table-container {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.readings-table {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.table-header {
  display: grid;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

.table-header.columns-3,
.table-row.columns-3 {
  grid-template-columns: 1.3fr 1fr 0.8fr;
}

.table-header.columns-4,
.table-row.columns-4 {
  grid-template-columns: 1.2fr 0.9fr 0.7fr 1fr;
}

.table-header.columns-5,
.table-row.columns-5 {
  grid-template-columns: 1.2fr 0.9fr 0.6fr 1fr 0.9fr;
}

.table-body {
  flex: 1;
  overflow: hidden;
}

.table-row {
  display: grid;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 11px;
  color: #CFCFCF;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.table-row:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.table-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 10px;
  display: flex;
  align-items: center;
}

.value-cell {
  font-weight: 600;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
}

.consumption-positive {
  color: #4ade80;
}

.consumption-negative {
  color: #f87171;
}

/* Badges */
.badge {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(74, 222, 128, 0.1);
  color: #4ade80;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
}

.badge-error {
  background: rgba(248, 113, 113, 0.1);
  color: #f87171;
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin-top: auto;
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-info {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.page-number:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

.page-number.active {
  background-color: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
  font-weight: 600;
}

/* States */
.loading-state,
.error-state,
.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.no-data-icon {
  font-size: 48px;
  opacity: 0.5;
}

.no-data-text {
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.no-data-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 300px;
  text-align: center;
}

.l {
  padding-left: 30px;
}

/* Responsive */
@media (max-width: 1200px) {
  .graph-table-row {
    grid-template-columns: 1fr;
  }

  .chart-section {
    height: 320px !important;
  }

  .values-table-container {
    height: auto !important;
    max-height: 400px;
  }
}

@media (max-width: 767.98px) {
  .section-header {
    margin-top: 60px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
  }

  .controls-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .granularity-tabs {
    width: 100%;
  }

  .granularity-tab {
    flex: 1;
    justify-content: center;
    padding: 10px 8px;
  }

  .tab-label {
    display: none;
  }

  .data-info {
    width: 100%;
    justify-content: flex-start;
  }

  .graph-table-row {
    grid-template-columns: 1fr;
  }

  .chart-section {
    height: 280px !important;
  }

  .values-table-container {
    height: auto !important;
    max-height: 350px;
    overflow-y: auto;
  }

  .table-header.columns-4,
  .table-row.columns-4 {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .table-header.columns-5,
  .table-row.columns-5 {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .table-cell:nth-child(4),
  .table-cell:nth-child(5) {
    display: none;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }

  .pagination-info {
    text-align: center;
  }

  .l {
    padding-left: 15px;
    padding-right: 15px;
  }
}
.table-cell.sortable {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.table-cell.sortable:hover {
  color: #4fc3f7;
}

.sort-icon {
  display: inline-flex;
  align-items: center;
  opacity: 0.7;
}

.table-cell.sortable:hover .sort-icon {
  opacity: 1;
}

/* Missing readings indicator */
.missing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-value.missing {
  color: #ef5350;
  font-weight: 600;
}

.missing-indicator .stat-label {
  color: #ef5350;
  opacity: 0.8;
}
</style>
